import { Agent } from "@/types/agent/agent";

export class AgentMapper {
  private static mapPhoneNumber(data: any) {
    const phoneNumbers = data.agent_phone_numbers || data.phone || [];
    const phoneData = phoneNumbers.find((p: any) => p.is_transfer !== true) || null;

    return this.mapPhoneRow(phoneData, data);
  }

  private static mapTransferPhones(data: any) {
    const phoneNumbers = data.agent_phone_numbers || data.phone || [];
    return phoneNumbers
      .filter((p: any) => p.is_transfer === true)
      .sort((a: any, b: any) => String(a.date_created ?? "").localeCompare(String(b.date_created ?? "")))
      .map((p: any) => this.mapPhoneRow(p, data))
      .filter(Boolean);
  }

  private static mapPhoneRow(phoneData: any, data: any) {
    if (!phoneData?.id) return null;

    return {
      id: phoneData.id || "",
      agent_id: phoneData.agent_id || null,
      el_agent_id: phoneData.el_agent_id || data.elevenlabs_id || null,
      number: phoneData.phone_number || phoneData.number || null,
      friendly_name: phoneData.friendly_name || null,
      elevenlabs_id: phoneData.metadata?.elevenlabs_id || phoneData.elevenlabs_id || null,
      twilio_sid: phoneData.phone_number_id || phoneData.twilio_sid || null,
      status: phoneData.status || null,
      country_code: phoneData.country_code || null,
      metadata: phoneData.metadata || null,
      created_at: phoneData.date_created || phoneData.created_at || null,
      updated_at: phoneData.date_created || phoneData.updated_at || null,
    };
  }

  public static map(data: any): Agent {
    return {
      id: data.id,
      user_id: data.user_id,
      name: data.name,
      description: data.description || null,
      brand: data.brand || null,
      elevenlabs_id: data.elevenlabs_id,
      is_active: data.is_active || false,
      is_setup: data.is_setup || false,
      category: data.category || null,
      category_data: data.category_data || null,
      voice: data.voice || data.settings?.conversation_config?.tts?.voice_id || null,
      voice_label: data.voice_label || null,
      send_conv_reports: data.send_conv_reports || null,
      report_mode: data.report_mode || (data.send_conv_reports ? "every_conversation" : "off"),
      report_digest_frequency: data.report_digest_frequency || null,
      report_digest_day_of_week: data.report_digest_day_of_week ?? null,
      escalation_settings: data.escalation_settings || null,
      report_recipients: (data.notification_recipients || [])
        .filter((r: any) => r.receives_reports && r.status === "active")
        .map((r: any) => r.email),
      escalation_recipients: (data.notification_recipients || [])
        .filter((r: any) => r.receives_escalations && r.status === "active")
        .map((r: any) => r.email),
      blocked_report_recipients: (data.notification_recipients || [])
        .filter((r: any) => r.receives_reports && r.status === "blocked")
        .map((r: any) => r.email),
      blocked_escalation_recipients: (data.notification_recipients || [])
        .filter((r: any) => r.receives_escalations && r.status === "blocked")
        .map((r: any) => r.email),
      connected_channels: data.connected_channels || [],
      redirect_url: data.redirect_url,
      capture_leads: data.capture_leads ?? false,
      settings: data.settings || null,
      branding: data.branding || null,
      position: data.position || 0,
      group_id: data.group_id || null,
      call_limit: data.call_limit || 10,
      created_at: data.created_at || null,
      updated_at: data.updated_at || null,
      persona_id: data.persona_id || null,
      is_public: data.is_public || false,
      is_demo_genie: data.is_demo_genie || false,
      is_marketplace_genie: data.is_marketplace_genie || false,
      saved_qr_codes: data.saved_qr_codes || [],
      memory_enabled: data.memory_enabled || false,
      purpose: data.purpose || "general",
      url_name: data.url_name || null,
      welcome_message: data.welcome_message || null,
      info_links: data.info_links || null,
      type: data.type || null,
      goal: data.goal || data.lead_info_presets || data.agent_lead_info?.[0]?.lead_info_presets || null,
      phone: this.mapPhoneNumber(data),
      transfer_phone: this.mapTransferPhones(data)[0] ?? null,
      transfer_phones: this.mapTransferPhones(data),
      // Genie Page (/help/:url_name) settings live on the agent row itself.
      hub_config: data.hub_config ?? null,
      hub_approved_at: data.hub_approved_at ?? null,
      // DEPRECATED: agent_pages table is obsolete and scheduled for deletion — do not add new usages.
      page: {
        id: data.page?.id || data.agent_pages?.[0]?.id || null,
        agent_id: data.page?.agent_id || data.id || null,
        el_agent_id: data.page?.el_agent_id || data.agent_pages?.[0]?.elevenlabs_agent_id || null,
        url_name: data.url_name || data.page?.url_name || data.agent_pages?.[0]?.url_name || null,
        welcome_message:
          data.welcome_message || data.page?.welcome_message || data.agent_pages?.[0]?.welcome_message || null,
        target_audience: data.page?.target_audience || data.agent_pages?.[0]?.audience_type || null,
        genie_image_url: data.page?.genie_image_url || data.agent_pages?.[0]?.genie_image_url || null,
        branding: data.page?.branding || data.agent_pages?.[0]?.branding || null,
        metadata:
          data.page?.metadata || {
            redirect_url: data.agent_pages?.[0]?.redirect_url,
          } ||
          null,
        sandbox_link: data.page?.sandbox_link || data.agent_pages?.[0]?.sandbox_link || null,
        info_links: data.info_links || data.page?.info_links || data.agent_pages?.[0]?.info_links || null,
        created_at: data.page?.created_at || data.agent_pages?.[0]?.created_at || null,
        updated_at: data.page?.updated_at || data.agent_pages?.[0]?.updated_at || null,
      },
      escalation_webhook: data.agent_escalation_webhooks?.[0]
        ? { is_active: data.agent_escalation_webhooks[0].is_active }
        : null,
      group: data.agent_groups || null,
      knowledge_base: (data.agent_document || [])
        .map((ad: any) =>
          ad.documents
            ? {
                id: ad.documents.id,
                name: ad.documents.name,
                is_active: ad.documents.is_active,
                el_doc_id: ad.documents.el_doc_id,
                status: ad.documents.status,
                source_path: ad.documents.source_path,
                parsed_path: ad.documents.parsed_path,
                optimized_path: ad.documents.optimized_path,
                source_type: ad.documents.source_type,
                created_at: ad.documents.created_at,
              }
            : null,
        )
        .filter(Boolean),
    };
  }
}
